/**
 * Billing account compact summary — identity bar for the account shell
 * below the `lg` breakpoint, where the inline desktop rail has no room.
 * Always shows the customer name and account status; expanding reveals the
 * same rail content (agreement management, notes) in a collapsible section
 * that pushes the tab strip down rather than opening a drawer — a billing
 * account has no operational stage that needs to stay pinned in view the
 * way a site dashboard's relay/camera controls do.
 *
 * Contains:
 * - AccountMobileSummary — mobile-only rail shell (default export)
 */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, ButtonBase, Collapse, Divider, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import StatusChip from '@sophocles/components/StatusChip';
import BillingAccountRail from '@sophocles/views/billing/account/BillingAccountRail';

function AccountMobileSummary({ account }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const detailsLabel = expanded ? t('actions.hideDetails', 'Hide details') : t('actions.showDetails', 'Show details');

  return (
    // One surface: the summary bar is the card's header row and expansion
    // grows the same card, so the rail content never sits bare on the page
    // canvas.
    <Box
      sx={{
        flexShrink: 0,
        mb: 'var(--page-gap)',
        bgcolor: 'var(--card-bg)',
        border: 1,
        borderColor: 'var(--card-border)',
        borderRadius: 'var(--card-radius)',
        boxShadow: 'var(--card-shadow)',
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        aria-label={`${detailsLabel}: ${account?.customer?.name ?? ''}`}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          p: 2,
          textAlign: 'left',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ minWidth: 0, fontWeight: 'var(--weight-semibold)' }}>
            {account?.customer?.name}
          </Typography>
          <StatusChip color="ok" label={t('billing.prepaidCoverage.statusActive')} />
        </Stack>
        <ExpandMoreIcon
          sx={{
            flexShrink: 0,
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--duration-normal) var(--easing-standard)',
          }}
        />
      </ButtonBase>
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <BillingAccountRail account={account} hideIdentity />
        </Box>
      </Collapse>
    </Box>
  );
}

export default AccountMobileSummary;
